import { ActivityFeedbackFrame } from 'subframe/components/ActivityFeedbackFrame';
import { Account } from 'models/account';
import { useState } from 'react';
import * as Sentry from '@sentry/browser';
import {
  isPlanActionDisabled,
  getUpgradePlanStatusBadge,
  getUpgradeTemplateStatusBadge,
  UpgradeViewMode,
  getUpgradeAssessmentStatusBadge,
} from './UpgradesHelpers';
import { AtomicTooltip } from 'components/design-system';
import { UPGRADES } from 'src/data/upgrades';
import {
  updateUpgradeAssessmentStageStep,
  updateUpgradePlanStageStep,
  updateUpgradeTemplateStageStep,
} from 'api/frontend';
import { Badge, Button } from 'subframe/index';
import * as SubframeCore from '@subframe/core';
import { useNavigate } from 'react-router';
import {
  UpdateUpgradePlanStageStepRequest,
  UpgradeAssessmentStatus,
  UpgradePlanStatus,
  UpgradeTemplateStatus,
} from 'api/models';

// copied from subframe/components/Button, not exportable
interface ButtonRootProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | 'brand-primary'
    | 'brand-secondary'
    | 'brand-tertiary'
    | 'neutral-primary'
    | 'neutral-secondary'
    | 'neutral-tertiary'
    | 'destructive-primary'
    | 'destructive-secondary'
    | 'destructive-tertiary'
    | 'inverse';
  size?: 'large' | 'medium' | 'small';
  children?: string;
  icon?: SubframeCore.IconName;
  iconRight?: SubframeCore.IconName;
  loading?: boolean;
  className?: string;
}
interface StepActionButtonProps extends ButtonRootProps {
  account: Account;
  mode: UpgradeViewMode;
  upgradeId: string;
  stageNumber: number;
  stepNumber: number;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  update: UpdateUpgradePlanStageStepRequest;
  onSuccess: () => void;
  upgradeStatus?:
    | UpgradePlanStatus
    | UpgradeTemplateStatus
    | UpgradeAssessmentStatus;
  isRedirect?: boolean;
  showActionButton?: boolean;
  basePath?: string;
  children: string;
}
export function StepActionButton({
  account,
  mode,
  upgradeId,
  stageNumber,
  stepNumber,
  isLoading,
  setIsLoading,
  update,
  children,
  onSuccess,
  upgradeStatus,
  isRedirect,
  showActionButton,
  basePath,
  ...otherProps
}: StepActionButtonProps): JSX.Element {
  const [variant, setVariant] = useState<'default' | 'success' | 'error'>(
    'default',
  );
  const showExampleData = UPGRADES.map((upgr) => upgr.id).includes(upgradeId)
    ? true
    : false;
  const navigate = useNavigate();

  if (!showActionButton && mode !== UpgradeViewMode.Plan && !isRedirect) {
    return <></>;
  }

  return (
    <ActivityFeedbackFrame variant={variant}>
      {isPlanActionDisabled(upgradeStatus) && !isRedirect ? (
        <AtomicTooltip
          tooltipContent={`Action not allowed on ${upgradeStatus} Upgrade Plan`}
        >
          <Button size="large" {...otherProps} disabled={true}>
            {children}
          </Button>
        </AtomicTooltip>
      ) : (
        <Button
          size="large"
          disabled={isLoading}
          loading={isLoading}
          onClick={async () => {
            if (!showExampleData) {
              if (isRedirect) {
                navigate(`${basePath}/${upgradeId}`);
                return;
              }
              setIsLoading(true);
              try {
                switch (mode) {
                  case UpgradeViewMode.Plan:
                    await updateUpgradePlanStageStep(
                      upgradeId,
                      stageNumber.toString(),
                      stepNumber.toString(),
                      update,
                      {
                        headers: { Authorization: `Bearer ${account.token}` },
                      },
                    );
                    break;
                  case UpgradeViewMode.Template:
                    await updateUpgradeTemplateStageStep(
                      upgradeId,
                      stageNumber.toString(),
                      stepNumber.toString(),
                      update,
                      {
                        headers: { Authorization: `Bearer ${account.token}` },
                      },
                    );
                    break;
                  case UpgradeViewMode.Assessment:
                    await updateUpgradeAssessmentStageStep(
                      upgradeId,
                      stageNumber.toString(),
                      stepNumber.toString(),
                      update,
                      {
                        headers: { Authorization: `Bearer ${account.token}` },
                      },
                    );
                    break;
                }

                onSuccess();
                // we'll wait here for a sec to give the backend some time
                await new Promise((f) => setTimeout(f, 3000));
                setVariant('success');
              } catch (err) {
                Sentry.captureException(err);
                setVariant('error');
              } finally {
                setIsLoading(false);
              }
            }
          }}
          {...otherProps}
        >
          {children}
        </Button>
      )}
    </ActivityFeedbackFrame>
  );
}

export function getUpgradeAnyStatusBadge(
  status: UpgradePlanStatus | UpgradeTemplateStatus | UpgradeAssessmentStatus,
  mode: UpgradeViewMode,
): JSX.Element {
  switch (mode) {
    case UpgradeViewMode.Plan: {
      const badge = getUpgradePlanStatusBadge(status as UpgradePlanStatus);
      return <Badge variant={badge.variant}>{badge.title}</Badge>;
    }
    case UpgradeViewMode.Template: {
      const badge = getUpgradeTemplateStatusBadge(
        status as UpgradeTemplateStatus,
      );
      return <Badge variant={badge.variant}>{badge.title}</Badge>;
    }
    case UpgradeViewMode.Assessment: {
      const badge = getUpgradeAssessmentStatusBadge(
        status as UpgradeAssessmentStatus,
      );
      return <Badge variant={badge.variant}>{badge.title}</Badge>;
    }
  }
}
